import { calledBy } from "../../me/called.ts";
import type { Candidate } from "../composer/mentions.ts";
import { runsOf } from "../composer/mentions.ts";

export type Taggable = { id: string; name: string; mediaType: string; bytes?: number };

export type Tag =
  | { kind: "person" | "agent"; who: Candidate }
  | { kind: "file"; name: string; file: Taggable | null }
  | { kind: "url"; href: string };

export type Piece = {
  at: number;
  text: string;
  tag: Tag | null;

  spaceAfter: boolean;
};

const A_FILE_TAG = /@\[([^\]\n]{1,255})\]/g;

const A_LINK = /https?:\/\/[^\s<>()[\]"']+/g;

const NOT_PART_OF_A_LINK = /[.,;:!?'")\]]+$/;

function tagsInWords(text: string, from: number, files: readonly Taggable[]): Piece[] {
  const found: Array<{ start: number; end: number; tag: Tag }> = [];
  for (const match of text.matchAll(A_FILE_TAG)) {
    const name = (match[1] ?? "").trim();
    const at = match.index ?? 0;
    found.push({
      start: at,
      end: at + match[0].length,

      tag: { kind: "file", name, file: files.find((one) => one.name === name) ?? null },
    });
  }
  for (const match of text.matchAll(A_LINK)) {
    const at = match.index ?? 0;
    const whole = match[0];
    const href = whole.replace(NOT_PART_OF_A_LINK, "");
    if (!href.includes("://") || href.length < 10) continue;

    if (found.some((one) => at >= one.start && at < one.end)) continue;
    found.push({ start: at, end: at + href.length, tag: { kind: "url", href } });
  }
  found.sort((a, b) => a.start - b.start);

  const pieces: Piece[] = [];
  let cut = 0;
  for (const one of found) {
    if (one.start < cut) continue;
    if (one.start > cut) {
      pieces.push({ at: from + cut, text: text.slice(cut, one.start), tag: null, spaceAfter: false });
    }

    const after = text[one.end] === " ";
    pieces.push({
      at: from + one.start,
      text: text.slice(one.start, one.end),
      tag: one.tag,
      spaceAfter: after,
    });
    cut = one.end + (after ? 1 : 0);
  }
  if (cut < text.length) {
    pieces.push({ at: from + cut, text: text.slice(cut), tag: null, spaceAfter: false });
  }
  return pieces;
}

export function piecesOf(text: string, names: readonly Candidate[], files: readonly Taggable[]): Piece[] {
  const pieces: Piece[] = [];
  for (const run of runsOf(text, names)) {
    if (run.mentions) {
      const kind = run.mentions.kind;
      const after = text[run.at + run.text.length] === " ";
      pieces.push({ at: run.at, text: run.text, tag: { kind, who: run.mentions }, spaceAfter: after });
      continue;
    }

    const eaten = pieces.at(-1)?.spaceAfter ? 1 : 0;
    const words = run.text.slice(eaten);
    if (words) pieces.push(...tagsInWords(words, run.at + eaten, files));
  }
  return pieces;
}

export function linkFace(href: string): { host: string; tail: string } {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return { host: href, tail: "" };
  }
  const host = url.hostname.replace(/^www\./, "");
  const parts = url.pathname.split("/").filter((one) => one.length > 0);
  const last = parts.at(-1) ?? "";
  let tail = "";
  try {
    tail = decodeURIComponent(last);
  } catch {
    tail = last;
  }

  if (tail.length > 24) tail = `${tail.slice(0, 23)}…`;
  return { host, tail };
}

export function faceOf(tag: Tag): string {
  if (tag.kind === "file") return tag.file ? tag.file.name : tag.name;
  if (tag.kind === "url") {
    const { host, tail } = linkFace(tag.href);
    return tail ? `${host}/${tail}` : host;
  }
  return calledBy(tag.who) ?? tag.who.handle;
}

export type Offer =
  | { kind: "person" | "agent"; key: string; who: Candidate }
  | { kind: "file"; key: string; file: Taggable }
  | { kind: "url"; key: string; href: string };

export function writtenFor(offer: Offer): string {
  if (offer.kind === "file") return `@[${offer.file.name}] `;
  if (offer.kind === "url") return `${offer.href} `;
  return `@${offer.who.handle} `;
}

export function withTag(
  text: string,
  asking: { at: number; fragment: string },
  offer: Offer,
  caret: number,
): { text: string; caret: number } {
  const upTo = Math.max(asking.at, Math.min(caret, text.length));
  const written = writtenFor(offer);
  return {
    text: text.slice(0, asking.at) + written + text.slice(upTo),
    caret: asking.at + written.length,
  };
}

function wordsOf(offer: Offer): string[] {
  if (offer.kind === "file") return [offer.file.name];
  if (offer.kind === "url") {
    const { host, tail } = linkFace(offer.href);
    return [host, tail, offer.href];
  }
  return [offer.who.handle, offer.who.displayName ?? ""];
}

export function linksSaid(texts: readonly string[], atMost = 8): string[] {
  const seen: string[] = [];
  for (let i = texts.length - 1; i >= 0; i--) {
    for (const match of (texts[i] ?? "").matchAll(A_LINK)) {
      const href = match[0].replace(NOT_PART_OF_A_LINK, "");
      if (href.includes("://") && !seen.includes(href)) seen.push(href);
      if (seen.length >= atMost) return seen;
    }
  }
  return seen;
}

export function offersFrom(
  people: readonly Candidate[],
  files: readonly Taggable[],
  links: readonly string[],
): Offer[] {
  return [
    ...people.map((who) => ({ kind: who.kind, key: `who-${who.account}`, who }) as Offer),
    ...files.map((file) => ({ kind: "file", key: `file-${file.id}`, file }) as Offer),
    ...links.map((href) => ({ kind: "url", key: `url-${href}`, href }) as Offer),
  ];
}

export const AT_MOST = 6;

const lower = (s: string): string => s.toLowerCase();

export function matchingTags(all: readonly Offer[], fragment: string): Offer[] {
  const want = lower(fragment.trim());
  const rank = (one: Offer): number => {
    if (want === "") return 0;
    let best = -1;
    for (const raw of wordsOf(one)) {
      const word = lower(raw);
      if (!word) continue;
      let here = -1;
      if (word === want) here = 0;
      else if (word.startsWith(want)) here = 1;
      else if (word.split(/[\s._/-]+/).some((part) => part.startsWith(want))) here = 2;
      else if (word.includes(want)) here = 3;
      if (here >= 0 && (best < 0 || here < best)) best = here;
    }
    return best;
  };
  return all
    .map((one, order) => ({ one, order, rank: rank(one) }))
    .filter((row) => row.rank >= 0)
    .sort((a, b) => a.rank - b.rank || a.order - b.order)
    .slice(0, AT_MOST)
    .map((row) => row.one);
}
